/**
 * deeplinks.js — Shared deep link resolution for all pages.
 *
 * Deep link URLs are fetched on-demand from the server API rather than
 * being sent on every odds record. Results are cached client-side so
 * repeat clicks on the same bet are instant.
 *
 * Handles:
 *   - Platform detection (iOS / Android / Desktop)
 *   - On-demand fetch of deep link URLs via REST API
 *   - Client-side caching of fetched URLs
 *   - Replacing placeholders (<STATE>, <COUNTRY>, <PARTNER_ID>)
 *   - Opening deep links in the correct context (iOS top frame vs new window)
 *
 * User's state and country are stored in localStorage and set via the app's settings UI.
 */

import { DEEPLINK_API_URL } from './config';

// ── Platform detection (runs once at import time) ──
const _getPlatform = () => {
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
  if (/android/i.test(ua)) return 'android';
  return 'desktop';
};

export const CLIENT_PLATFORM = _getPlatform();

// ── Placeholder resolution ──
const PLACEHOLDER_MAP = {
  '<PARTNER_ID>': 'sharpstack',
};

const resolveUrl = (url) => {
  if (!url) return null;
  const state = localStorage.getItem('userState') || 'nj';
  const country = localStorage.getItem('userCountry') || 'us';
  return url
    .replace(/<STATE>/g, state.toLowerCase())
    .replace(/<COUNTRY>/g, country.toLowerCase())
    .replace(/<PARTNER_ID>/g, PLACEHOLDER_MAP['<PARTNER_ID>']);
};

// ── Pick the best URL for the current platform ──
const _pickPlatformUrl = (data) => {
  if (!data) return null;
  if (CLIENT_PLATFORM === 'ios') {
    return data.ios || data.desktop || data.android || null;
  } else if (CLIENT_PLATFORM === 'android') {
    return data.android || data.desktop || data.ios || null;
  }
  return data.desktop || data.ios || data.android || null;
};

// ── Client-side cache for fetched deep links ──
const _cache = new Map();

// ── Fetch and resolve a deep link for a given odds ID ──
export const fetchDeepLink = async (oddsId) => {
  if (!oddsId) return null;

  // Return cached result if available
  if (_cache.has(oddsId)) return _cache.get(oddsId);

  try {
    const resp = await fetch(`${DEEPLINK_API_URL}/${encodeURIComponent(oddsId)}`);
    if (!resp.ok) {
      _cache.set(oddsId, null);
      return null;
    }
    const data = await resp.json();
    const picked = _pickPlatformUrl(data);
    const url = resolveUrl(picked);
    _cache.set(oddsId, url);
    return url;
  } catch (err) {
    return null;
  }
};

// ── Open a deep link ──
export const openDeepLink = (url) => {
  if (!url) return;
  if (CLIENT_PLATFORM === 'ios') {
    // iOS within an iframe needs top-level navigation for app deep links
    window.top.location.href = url;
  } else {
    window.open(url, '_blank', 'width=1000,height=800,scrollbars=yes');
  }
};

// ── Build combined PrizePicks betslip URL from individual deep links ──
// Fetches deep links for all odds IDs, extracts the projections= parameter
// from each, and combines them into a single URL.
// e.g. https://app.prizepicks.com/?projections=10439069-o-1.5,10453990-u-3.5
export const buildPrizePicksSlipUrl = async (oddsIds) => {
  if (!oddsIds || oddsIds.length === 0) return null;

  const urls = await Promise.all(oddsIds.map(id => fetchDeepLink(id)));

  const projections = urls
    .filter(url => url && url.includes('projections='))
    .map(url => {
      const match = url.match(/projections=([^&]+)/);
      return match ? match[1] : null;
    })
    .filter(Boolean);

  if (projections.length === 0) return null;
  return `https://app.prizepicks.com/?projections=${projections.join(',')}`;
};